<template>
  <div
    class="min-h-screen overflow-x-hidden bg-bochas-bg px-3 py-6 sm:px-4 sm:py-8"
  >
    <div class="w-full min-w-0 max-w-7xl mx-auto">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8"
      >
        <h1 class="text-2xl sm:text-3xl font-bold text-white">
          {{ isEdit ? "Editar evento" : "Nuevo evento" }}
        </h1>
        <router-link
          to="/admin/calendario"
          class="btn-bochas-trans text-sm self-start sm:self-auto"
        >
          ← Volver
        </router-link>
      </div>

      <form @submit.prevent="onSave" class="w-full min-w-0 space-y-6">
        <!-- ============ DATOS BÁSICOS ============ -->
        <div class="card-fbochas min-w-0 p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Datos básicos</h2>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Nombre del torneo *</label
            >
            <input
              v-model="form.title"
              type="text"
              required
              class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              placeholder="Ej: Torneo River Plate de Bell Ville"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Descripción / Info adicional</label
            >
            <RichTextEditor
              v-model="form.additionalInfo"
              placeholder="Ej: Se jugará bajo reglamento oficial. Uniforme obligatorio."
            />
          </div>

          <div class="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="min-w-0">
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Fecha *</label
              >
              <DatePickerField
                v-model="form.appointmentDay"
                name="appointment-day"
                placeholder="Fecha"
                fixed-dropdown
              />
            </div>
            <div class="min-w-0">
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Hora de inicio</label
              >
              <input
                v-model="form.appointmentTime"
                type="time"
                class="ios-date-time-input w-full min-w-0 max-w-full px-3 sm:px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-rojo transition-colors"
              />
            </div>
          </div>

          <div class="grid min-w-0 grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Club organizador</label
              >
              <SelectField
                :model-value="organizerClubSelectValue"
                :options="clubOptions"
                placeholder="Sin club"
                @update:modelValue="onOrganizerClubChange"
              />
            </div>
            <div class="min-w-0">
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Asociación</label
              >
              <SelectField
                :model-value="asociacionSelectValue"
                :options="asociacionOptions"
                placeholder="Sin asociación"
                @update:modelValue="onAsociacionChange"
              />
            </div>
            <div class="min-w-0">
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Localidad</label
              >
              <input
                v-model="form.location"
                type="text"
                class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
                placeholder="Ej: Bell Ville"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Ubicación (dirección)</label
            >
            <input
              v-model="form.address"
              type="text"
              class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              placeholder="Ej: Mendoza 388"
            />
          </div>
        </div>

        <!-- ============ SORTEO ============ -->
        <div class="card-fbochas min-w-0 p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Sorteo</h2>
          <p class="text-xs text-gray-400 -mt-2">
            Si no hay sorteo, dejá estos campos vacíos.
          </p>
          <div class="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="min-w-0">
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Fecha del sorteo</label
              >
              <DatePickerField
                v-model="form.sorteoDate"
                name="sorteo-date"
                placeholder="Fecha del sorteo"
                fixed-dropdown
              />
            </div>
            <div class="min-w-0">
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Hora del sorteo</label
              >
              <input
                v-model="form.sorteoTime"
                type="time"
                class="ios-date-time-input w-full min-w-0 max-w-full px-3 sm:px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-rojo transition-colors"
              />
            </div>
          </div>
        </div>

        <!-- ============ CATEGORÍAS ============ -->
        <div class="card-fbochas min-w-0 p-4 sm:p-6 space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-white">Categorías</h2>
            <button
              type="button"
              @click="addCategory"
              class="btn-bochas-azul text-xs"
            >
              + Agregar
            </button>
          </div>
          <div
            v-if="form.categories.length === 0"
            class="text-sm text-gray-400 text-center py-3"
          >
            Sin categorías. Agregá al menos una (ej: Individual 1° Categoría).
          </div>
          <div
            v-for="(_, idx) in form.categories"
            :key="idx"
            class="flex items-center gap-2"
          >
            <div class="min-w-0 flex-1">
              <SelectField
                v-model="form.categories[idx]"
                :options="categoryOptionsFor(form.categories[idx])"
                placeholder="Seleccioná una categoría"
              />
            </div>
            <button
              type="button"
              @click="removeCategory(idx)"
              class="text-red-400 hover:text-red-300 text-sm px-2"
            >
              ✕
            </button>
          </div>
        </div>

        <!-- ============ CONTACTOS ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-white">Contactos</h2>
            <button
              type="button"
              @click="addContact"
              class="btn-bochas-azul text-xs"
            >
              + Agregar
            </button>
          </div>
          <div
            v-if="form.contacts.length === 0"
            class="text-sm text-gray-400 text-center py-3"
          >
            Sin contactos. Agregá al menos uno (ej: Luis - 3564-661842).
          </div>
          <div
            v-for="(_, idx) in form.contacts"
            :key="idx"
            class="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2"
          >
            <input
              v-model="form.contacts[idx].name"
              type="text"
              maxlength="100"
              class="w-full rounded-lg border border-gray-700 bg-bochas-bg px-4 py-3 text-left text-white placeholder-gray-500 transition-colors focus:border-bochas-rojo focus:outline-none"
              placeholder="Nombre (ej: Luis)"
            />
            <SelectField
              :model-value="form.contacts[idx].phone"
              :options="contactPhoneOptions"
              allow-custom
              :maxlength="40"
              placeholder="Teléfono (ej: 3564-661842)"
              @update:modelValue="onContactChange(idx, 'phone', $event)"
            />
            <button
              type="button"
              @click="removeContact(idx)"
              class="text-red-400 hover:text-red-300 text-sm px-2 self-center justify-self-center"
            >
              ✕
            </button>
          </div>
          <p v-if="contactsError" class="text-sm text-red-300" role="alert">
            {{ contactsError }}
            <button type="button" class="underline ml-2" @click="loadContactos">Reintentar</button>
          </p>
        </div>

        <!-- ============ REGLAMENTO (PDF) ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Reglamento (PDF)</h2>

          <div
            v-if="reglamentoActual"
            class="bg-white/5 rounded-lg p-3 flex items-center justify-between gap-3"
          >
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-6 h-6 text-bochas-rojo flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <div class="min-w-0 flex-1">
                <p class="text-white text-sm truncate">
                  {{ reglamentoActual.original_name }}
                </p>
                <a
                  :href="reglamentoActual.url"
                  target="_blank"
                  rel="noopener"
                  class="text-bochas-rojo text-xs hover:underline"
                  >Ver PDF →</a
                >
              </div>
            </div>
            <button
              type="button"
              @click="onDeleteReglamento"
              class="text-red-400 hover:text-red-300 text-xs px-2 flex-shrink-0"
            >
              Eliminar
            </button>
          </div>

          <div class="flex items-center gap-3 flex-wrap">
            <label
              class="btn-bochas-azul text-sm cursor-pointer inline-flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              {{ reglamentoActual ? "Reemplazar PDF" : "Subir PDF" }}
              <input
                type="file"
                accept="application/pdf"
                class="hidden"
                @change="onUploadReglamento"
              />
            </label>
            <span v-if="uploadingReglamento" class="text-xs text-gray-400"
              >Subiendo...</span
            >
          </div>
          <p v-if="reglamentoError" class="text-xs text-bochas-rojo">
            {{ reglamentoError }}
          </p>
          <p v-if="false" class="text-xs text-gray-500">
            Guardá el evento primero para poder subir el reglamento.
          </p>
          <p v-if="!isEdit && reglamentoFile" class="text-xs text-gray-500">
            El reglamento se va a subir al guardar el evento.
          </p>
        </div>

        <!-- ============ ESTADO ============ -->
        <div class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Estado</h2>
          <div class="flex items-center gap-4 sm:gap-6 flex-wrap">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="draft"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm">Borrador</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="published"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm">Publicado</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="cancelled"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm">Cancelado</span>
            </label>
          </div>
        </div>

        <div
          v-if="error"
          class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
        >
          {{ error }}
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            :disabled="saving"
            class="btn-bochas-rojo"
            :class="{ 'opacity-60 cursor-not-allowed': saving }"
          >
            <span v-if="!saving">{{
              isEdit ? "Guardar cambios" : "Crear evento"
            }}</span>
            <span v-else>Guardando...</span>
          </button>
          <router-link
            to="/admin/calendario"
            class="btn-bochas-trans text-center"
          >
            Cancelar
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  crearEvento,
  editarEvento,
  obtenerEvento,
  subirReglamento,
  eliminarReglamento,
  listarContactosEventos,
} from "@/services/appointments/appointmentsService";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import { listarClubesAdmin } from "@/services/clubes/clubesService";
import { listarCategoriasCatalogo } from "@/services/tournaments/tournamentsService";
import DatePickerField from "@/components/Common/DatePickerField.vue";
import SelectField from "@/components/Common/SelectField.vue";
import RichTextEditor from "@/components/Common/RichTextEditor.vue";
import { toDateInputValue } from "@/utils/date";
import { useConfirm } from "@/composables/useConfirm";

const { confirm } = useConfirm();
const route = useRoute();
const router = useRouter();

const isEdit = computed(() => !!route.params.id);
const eventoId = computed(() => parseInt(route.params.id, 10));

const form = reactive({
  title: "",
  additionalInfo: "",
  appointmentDay: "",
  appointmentTime: "",
  location: "",
  asociacionId: null,
  organizerClub: "",
  address: "",
  sorteoDate: "",
  sorteoTime: "",
  status: "published",
  contacts: [],
  categories: [],
});

const saving = ref(false);
const error = ref("");

const reglamentoActual = ref(null);
const reglamentoFile = ref(null);
const uploadingReglamento = ref(false);
const reglamentoError = ref("");
const asociaciones = ref([]);
const clubes = ref([]);

const asociacionSelectValue = computed(() =>
  form.asociacionId ? String(form.asociacionId) : "",
);

const asociacionOptions = computed(() => [
  { value: "", label: "Sin asociación" },
  ...asociaciones.value.map((asociacion) => ({
    value: String(asociacion.id),
    label: asociacion.name,
  })),
]);

const selectedOrganizerClubId = ref("");

const selectedClubMissing = computed(
  () =>
    !!form.organizerClub &&
    !clubes.value.some((club) => club.name === form.organizerClub),
);
const organizerClubSelectValue = computed(() => {
  const selected = clubes.value.find(
    (club) => selectedOrganizerClubId.value
      ? String(club.id) === selectedOrganizerClubId.value
      : club.name === form.organizerClub &&
        (!form.asociacionId || Number(club.asociacionId) === Number(form.asociacionId)),
  );
  return selected ? String(selected.id) : form.organizerClub;
});

const clubOptions = computed(() => {
  const filtered = form.asociacionId
    ? clubes.value.filter(
        (club) => Number(club.asociacionId) === Number(form.asociacionId),
      )
    : clubes.value;
  const options = [{ value: "", label: "Sin club" }];
  if (selectedClubMissing.value) {
    options.push({ value: form.organizerClub, label: form.organizerClub });
  }
  return options.concat(
    filtered.map((club) => ({
      value: String(club.id),
      label: club.locality ? `${club.name} · ${club.locality}` : club.name,
    })),
  );
});

const categoryOptions = ref([]);

async function loadCategorias() {
  try {
    const data = await listarCategoriasCatalogo();
    const categorias = Array.isArray(data) ? data : data.items || [];
    categoryOptions.value = categorias.map((category) => ({
      value: category.name,
      label: category.name,
    }));
  } catch (err) {
    error.value = "No se pudieron cargar las categorías.";
  }
}

function categoryOptionsFor(value) {
  if (!value || categoryOptions.value.some((option) => option.value === value)) {
    return categoryOptions.value;
  }
  return [{ value, label: value }, ...categoryOptions.value];
}

async function loadAsociaciones() {
  try {
    const data = await listarAsociacionesAdmin();
    asociaciones.value = data.items || [];
  } catch (_) {
    asociaciones.value = [];
  }
}

async function loadClubes() {
  try {
    const data = await listarClubesAdmin();
    clubes.value = data.items || [];
  } catch (_) {
    clubes.value = [];
  }
}

function onAsociacionChange(value) {
  form.asociacionId = value ? Number(value) : null;
  form.organizerClub = "";
  selectedOrganizerClubId.value = "";
  const selected = asociaciones.value.find(
    (asociacion) => Number(asociacion.id) === Number(form.asociacionId),
  );
  if (selected?.locality) {
    form.location = selected.locality;
  }
}

function onOrganizerClubChange(value) {
  selectedOrganizerClubId.value = "";
  if (!value) {
    form.organizerClub = "";
    return;
  }
  const selected = clubes.value.find(
    (club) => String(club.id) === String(value),
  );
  if (!selected) {
    form.organizerClub = value;
    return;
  }
  selectedOrganizerClubId.value = String(selected.id);
  form.organizerClub = selected.name || "";
  form.asociacionId = selected.asociacionId || null;
  if (selected.locality) form.location = selected.locality;
  if (selected.address) form.address = selected.address;
}

function addCategory() {
  form.categories.push("");
}

function removeCategory(idx) {
  form.categories.splice(idx, 1);
}

function addContact() {
  form.contacts.push({ name: "", phone: "" });
}

const savedContacts = ref([]);
const contactsError = ref("");
const contactPhoneOptions = computed(() => savedContacts.value.map((contact) => ({
  value: `contact:${contact.id}`,
  label: contact.phone,
  inputLabel: contact.phone,
})));

function onContactChange(index, field, value) {
  const selected = savedContacts.value.find((contact) => `contact:${contact.id}` === value);
  if (selected) {
    form.contacts[index].phone = selected.phone;
  } else {
    form.contacts[index][field] = value || "";
  }
}

async function loadContactos() {
  contactsError.value = "";
  try {
    const data = await listarContactosEventos();
    savedContacts.value = data.items || [];
  } catch (_) {
    contactsError.value = "No se pudieron cargar los contactos guardados.";
  }
}

function removeContact(idx) {
  form.contacts.splice(idx, 1);
}

async function loadEvento() {
  if (!isEdit.value) return;
  try {
    const data = await obtenerEvento(eventoId.value);
    Object.assign(form, {
      title: data.title || "",
      additionalInfo: data.additionalInfo || "",
      appointmentDay: toDateInputValue(data.appointmentDay),
      appointmentTime: data.appointmentTime || "",
      location: data.location || "",
      asociacionId: data.asociacionId || null,
      organizerClub: data.organizerClub || "",
      address: data.address || "",
      sorteoDate: toDateInputValue(data.sorteoDate),
      sorteoTime: data.sorteoTime || "",
      status: data.status || "published",
      contacts: Array.isArray(data.contacts) ? [...data.contacts] : [],
      categories: Array.isArray(data.categories) ? [...data.categories] : [],
    });
    reglamentoActual.value = data.reglamento || null;
  } catch (err) {
    error.value = "No se pudo cargar el evento.";
  }
}

async function onSave() {
  saving.value = true;
  error.value = "";
  try {
    const payload = {
      title: form.title,
      additionalInfo: form.additionalInfo || null,
      appointmentDay: form.appointmentDay,
      appointmentTime: form.appointmentTime || null,
      location: form.location || null,
      asociacionId: form.asociacionId || null,
      organizerClub: form.organizerClub || null,
      address: form.address || null,
      sorteoDate: form.sorteoDate || null,
      sorteoTime: form.sorteoTime || null,
      status: form.status,
      contacts: form.contacts.map((c) => ({ name: c.name.trim(), phone: c.phone.trim() }))
        .filter((c) => c.name || c.phone),
      categories: form.categories.filter((c) => c.trim()),
    };

    let savedEvento = null;
    if (isEdit.value) {
      await editarEvento(eventoId.value, payload);
    } else {
      savedEvento = await crearEvento(payload);
    }

    const targetId = isEdit.value ? eventoId.value : savedEvento?.id;
    if (reglamentoFile.value && targetId) {
      await subirReglamento(targetId, reglamentoFile.value);
    }
    router.push("/admin/calendario");
  } catch (err) {
    error.value =
      err.response?.data?.message || "No se pudo guardar el evento.";
  } finally {
    saving.value = false;
  }
}

async function onUploadReglamento(e) {
  const file = e.target.files[0];
  if (!file) return;
  reglamentoFile.value = null;
  if (file.type !== "application/pdf") {
    reglamentoError.value = "Solo se permiten archivos PDF.";
    e.target.value = "";
    return;
  }
  reglamentoError.value = "";

  if (!isEdit.value) {
    reglamentoFile.value = file;
    return;
  }

  uploadingReglamento.value = true;
  try {
    const res = await subirReglamento(eventoId.value, file);
    reglamentoActual.value = res;
  } catch (err) {
    reglamentoError.value =
      err.response?.data?.message || "Error al subir el PDF.";
  } finally {
    uploadingReglamento.value = false;
    e.target.value = "";
  }
}

async function onDeleteReglamento() {
  const confirmed = await confirm({
    title: "Eliminar reglamento",
    message: "¿Eliminar el reglamento?",
  });
  if (!confirmed) return;
  try {
    await eliminarReglamento(eventoId.value);
    reglamentoActual.value = null;
  } catch (err) {
    reglamentoError.value = "Error al eliminar el reglamento.";
  }
}

onMounted(async () => {
  await Promise.all([loadAsociaciones(), loadClubes(), loadCategorias(), loadEvento(), loadContactos()]);
});
</script>

<style scoped>
@media (max-width: 640px) {
  .ios-date-time-input {
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    font-size: 0.875rem;
    appearance: none;
    -webkit-appearance: none;
    overflow: hidden;
  }

  .ios-date-time-input::-webkit-date-and-time-value {
    display: block;
    width: 100%;
    min-width: 0;
    text-align: left;
  }

  .ios-date-time-input::-webkit-calendar-picker-indicator {
    display: none;
    width: 0;
    margin-left: 0;
    padding: 0;
  }
}
</style>
